import React, { createContext, useEffect, useState } from "react";
import { isStrEmpty, isObjectEmpty } from '@eforms/forms-common-ui';
import ScreenActivityIndicator from "../feedback/ScreenActivityIndicator";
import {
  extractTokenFromURL,
  fetchIAMAccessToken,
  removeSessionItem,
  getLWBAccessToken,
  renewIAMToken,
  saveAccessToken,
  SESSION_STORAGE_KEY_ACCESS_TOKEN,
  SESSION_STORAGE_KEY_CHANNEL,
} from "../../utils/authUtils";

export const AUTHENTICATION_STATUS = {
  IN_PROGRESS: "IN_PROGRESS",
  SUCCESS: "SUCCESS",
  FAILURE: "FAILURE",
  NOT_REQUIRED: "NOT_REQUIRED",
};

export const AuthenticationContext = createContext(null);

export const IAMAuthenticationProvider = ({ children, match }) => {
  let formUuid = match.params.uuid;
  const MAX_TOKEN_RENEWALS = 3;
  const TOKEN_RENEWAL_INTERVAL = 14; // 14 minutes
  const SESSION_EXPIRES_ATTRIBUTE_NAME = "sessionExpiresAt";

  const [authenticationDetails, setAuthenticationDetails] = useState({
    authenticationStatus: AUTHENTICATION_STATUS.NOT_REQUIRED,
    authenticationError: null,
    showLimitedTimeWarning: false,
  });

  const [renewalsLeft, setRenewalsLeft] = useState(MAX_TOKEN_RENEWALS);
  const [renewalTimerId, setRenewalTimeId] = useState(null);
  const [renewalCountdown, setRenewalCountdown] = useState(
    TOKEN_RENEWAL_INTERVAL
  );

  const [authenticationComplete, setAuthenticationComplete] = useState(false);

  const updateAuthenticationDetails = (updatedDetails) => {
    setAuthenticationDetails((previousAuthDetails) => ({
      ...previousAuthDetails,
      ...updatedDetails,
    }));
  };

  const startRenewalCountdown = () => {
    setRenewalTimeId(
      setInterval(() => {
        setRenewalCountdown((countdownMinutes) => countdownMinutes - 1);
      }, 60 * 1000)
    );
  };

  const saveSessionExpiryTime = () => {
    sessionStorage.setItem(
      SESSION_EXPIRES_ATTRIBUTE_NAME,
      Date.now() + TOKEN_RENEWAL_INTERVAL * 60 * 1000
    );
  };

  useEffect(() => {
    const renewToken = async () => {
      // Time for renewal
      if (renewalCountdown === 0) {
        // Max renewal attempts not reached
        if (renewalsLeft > 0) {
          setRenewalsLeft((previousRenewalsLeft) => previousRenewalsLeft - 1);
          clearInterval(renewalTimerId);
          try {
            const newAccessToken = await renewIAMToken();
            saveAccessToken(newAccessToken);
            setRenewalCountdown(TOKEN_RENEWAL_INTERVAL);
            startRenewalCountdown();
            saveSessionExpiryTime();
          } catch (error) {
            setAuthenticationDetails((previousAuthDetails) => ({
              ...previousAuthDetails,
              authenticationError: "API_ERROR",
            }));
          }
        }
      }
      // Max renewal attempts reached and only 5 mins left for session to expire. Show limited time warning modal now.
      if (renewalsLeft === 0 && renewalCountdown === 5) {
        setAuthenticationDetails((previousAuthDetails) => ({
          ...previousAuthDetails,
          showLimitedTimeWarning: true,
        }));
        clearInterval(renewalTimerId);
      }
    };

    const initiateRenew = async () => {
      await renewToken();
    };
    initiateRenew();
  }, [renewalCountdown]);

  useEffect(() => {
    const authenticate = async (idToken, codeVerifier) => {
      try {
        const accessToken = await fetchIAMAccessToken(idToken, codeVerifier);
        saveAccessToken(accessToken);
        startRenewalCountdown();
        setAuthenticationDetails((previousAuthDetails) => ({
          ...previousAuthDetails,
          authenticationStatus: AUTHENTICATION_STATUS.SUCCESS,
        }));
        saveSessionExpiryTime();
      } catch (error) {
        setAuthenticationDetails((previousAuthDetails) => ({
          ...previousAuthDetails,
          authenticationStatus: AUTHENTICATION_STATUS.FAILURE,
          authenticationError: "AUTHENTICATION_FAILURE",
        }));
      }
    };

    const initiateAuthentication = async () => {
      // Remove if the old token available in the browser session
      removeSessionItem(SESSION_STORAGE_KEY_ACCESS_TOKEN);
      removeSessionItem(SESSION_STORAGE_KEY_CHANNEL);
      // if a user navigated from the Mobile then we will get idToken value else null
      let idToken = extractTokenFromURL();
      let error = "";
      let codeVerifier = "";
      if(isStrEmpty(idToken)) {
        // If form is authenticated and navigated from the LWB, we have to extract the id token from url and get the authCode to generate the auth token
        await getLWBAccessToken(formUuid).then((resp) => {
          if(!isObjectEmpty(resp)) {
            idToken = resp.code;
            error = resp.error;
            codeVerifier = resp.codeVerifier;
          }
        });
      }
      
      if (!isStrEmpty(idToken)) {
        setAuthenticationDetails((previousAuthDetails) => ({
          ...previousAuthDetails,
          authenticationStatus: AUTHENTICATION_STATUS.IN_PROGRESS,
        }));
        await authenticate(idToken, codeVerifier);
      } else if (!isStrEmpty(error)) {
        sessionStorage.clear();
        setAuthenticationDetails((previousAuthDetails) => ({
          ...previousAuthDetails,
          authenticationStatus: AUTHENTICATION_STATUS.FAILURE,
          authenticationError: "AUTHENTICATION_FAILURE",
        }));
      }
      setAuthenticationComplete(true);
    };

    initiateAuthentication();
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden || !document.msHidden || !document.webkitHidden) {
        const sessionExpiryMs = parseInt(sessionStorage.getItem(SESSION_EXPIRES_ATTRIBUTE_NAME));
        const currentTimeMs = Date.now();
        if (currentTimeMs > sessionExpiryMs) {
          setAuthenticationDetails((previousAuthDetails) => ({
            ...previousAuthDetails,
            authenticationError: "SESSION_EXPIRED",
          }));
          clearInterval(renewalTimerId);
        }
      }
    };
    if (authenticationDetails.authenticationStatus === AUTHENTICATION_STATUS.SUCCESS) {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [authenticationDetails.authenticationStatus]);

  return (
    <AuthenticationContext.Provider
      value={{
        authenticationDetails,
        updateAuthenticationDetails,
        renewalTimerId,
      }}
    >
      {authenticationComplete ? children : <ScreenActivityIndicator />}
    </AuthenticationContext.Provider>
  );
};
